import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  UserCheck,
  UserX,
  Plus,
  RussianRuble,
  Check,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Filter,
  KeyRound,
  Shield,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import DateRangePicker from "./DateRangePicker";
import AdminAnalytics from "./AdminAnalytics";
import PageHeader from "./PageHeader";
import { useAuth } from "./AuthContext";
import { isCivilDocType, civilDocLabel } from "./civilDocs";
import "./admin.css";

const docTypeLabel = (type) =>
  type === "passport"
    ? "Паспорт"
    : type === "drivers_license"
      ? "Вод. удостоверение"
      : type === "contract"
        ? "Договор / Документ"
        : isCivilDocType(type)
          ? civilDocLabel(type)
          : type;

/* ---------- маленькие куски разметки ---------- */

const Kpi = ({ label, value, sub, tone, icon: Icon }) => (
  <div className={`adm-kpi${tone ? ` tone-${tone}` : ""}`}>
    <span className="adm-kpi-label">
      {Icon && <Icon size={16} aria-hidden="true" />}
      {label}
    </span>
    <span className="adm-kpi-value">{value}</span>
    {sub && <span className="adm-kpi-sub">{sub}</span>}
  </div>
);

const AdminDashboard = () => {
  const { user: currentUser, checkAuth } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("analytics");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [documents, setDocuments] = useState([]);
  const [documentsTotals, setDocumentsTotals] = useState(null);
  const [documentsPagination, setDocumentsPagination] = useState(null);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  const [usdRubRate, setUsdRubRate] = useState(80);
  const [editingRate, setEditingRate] = useState(false);
  const [newRate, setNewRate] = useState("80");
  const [pageCost, setPageCost] = useState(50);
  const [editingPageCost, setEditingPageCost] = useState(false);
  const [newPageCost, setNewPageCost] = useState("50");
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [companiesList, setCompaniesList] = useState([]);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/users`);
      setUsers(response.data);

      const companies = [];
      const handledCompanies = new Set();
      response.data.forEach((u) => {
        if (u.company && !handledCompanies.has(u.company.id)) {
          companies.push({
            id: u.company.id,
            name: u.company.name,
            inn: u.company.inn,
          });
          handledCompanies.add(u.company.id);
        }
      });
      setCompaniesList(companies.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError("Не удалось загрузить данные пользователей");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        page: documentsPage,
        per_page: 50,
      };
      if (selectedCompanyId) {
        params.company_id = selectedCompanyId;
      }
      const response = await axios.get(`${API_BASE_URL}/api/admin/documents`, {
        params,
      });
      setDocuments(response.data.documents || []);
      setDocumentsTotals(response.data.totals || null);
      setDocumentsPagination(response.data.pagination || null);
    } catch (err) {
      console.error("Failed to fetch documents", err);
    } finally {
      setDocumentsLoading(false);
    }
  }, [API_BASE_URL, dateRange, selectedCompanyId, documentsPage]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/settings`);
      const settings = response.data;
      const rateSetting = settings.find((s) => s.key === "usd_rub_rate");
      if (rateSetting) {
        setUsdRubRate(parseFloat(rateSetting.value));
        setNewRate(rateSetting.value);
      }
      const pageCostSetting = settings.find((s) => s.key === "page_cost");
      if (pageCostSetting) {
        setPageCost(parseFloat(pageCostSetting.value));
        setNewPageCost(pageCostSetting.value);
      }
    } catch (err) {
      console.error("Failed to fetch settings", err);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchUsers();
    fetchSettings();
  }, [fetchUsers, fetchSettings]);

  useEffect(() => {
    if (activeTab === "documents") {
      fetchDocuments();
    }
  }, [activeTab, dateRange, selectedCompanyId, fetchDocuments]);

  // Filter changes must restart paging, otherwise page 7 of the old filter
  // shows an empty table for the new one.
  useEffect(() => {
    setDocumentsPage(1);
  }, [dateRange, selectedCompanyId]);

  const updateRate = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/settings/usd_rub_rate`, {
        value: newRate,
      });
      setUsdRubRate(parseFloat(newRate));
      setEditingRate(false);
    } catch (err) {
      alert("Ошибка при обновлении курса");
    }
  };

  const updatePageCost = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/settings/page_cost`, {
        value: newPageCost,
      });
      setPageCost(parseFloat(newPageCost));
      setEditingPageCost(false);
    } catch (err) {
      alert("Ошибка при обновлении стоимости страницы");
    }
  };

  const handleStatusChange = async (userId, status) => {
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/admin/users/${userId}/status`,
        { status },
      );
      fetchUsers();
      if (data.notified && !data.notified.email) {
        alert(
          data.notified.telegram
            ? "Доступ открыт. Письмо не ушло, но клиенту пришло сообщение в Telegram."
            : "Доступ открыт, но уведомить клиента не удалось (ни почта, ни Telegram). Сообщите ему вручную.",
        );
      }
    } catch (err) {
      alert("Ошибка при изменении статуса");
    }
  };

  /** Зачисление (amount > 0) или списание (amount < 0) с баланса. */
  const handleTopup = async (userId, amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n === 0) return;
    try {
      await axios.post(`${API_BASE_URL}/api/admin/users/${userId}/balance`, {
        amount: n,
      });
      fetchUsers();
    } catch (err) {
      alert("Ошибка при изменении баланса");
    }
  };

  const handlePriceUpdate = async (companyId, price) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/companies/${companyId}/price`,
        { price },
      );
      fetchUsers();
      alert("Цены успешно обновлены");
    } catch (err) {
      alert("Ошибка при обновлении цен");
    }
  };

  const handleChangePassword = async (userId, email) => {
    const newPassword = window.prompt(
      `Новый пароль для ${email}:\n(минимум 6 символов)`,
    );
    if (newPassword === null) return;
    if (newPassword.trim().length < 6) {
      alert("Пароль должен содержать не менее 6 символов");
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/users/${userId}/password`,
        { password: newPassword.trim() },
      );
      alert(`Пароль для ${email} успешно изменён`);
    } catch (err) {
      alert(
        err?.response?.data?.error || "Ошибка при изменении пароля",
      );
    }
  };

  const handleDeleteUser = async (userId) => {
    if (
      !window.confirm(
        "Удалить этого пользователя и все его данные? Это действие нельзя отменить.",
      )
    )
      return;
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}`);
      fetchUsers();
    } catch (err) {
      const raw = err?.response?.data?.error || err?.response?.data?.message;
      const msg =
        raw === "Cannot delete admin user"
          ? "Нельзя удалить учётную запись администратора. Сначала снимите флаг администратора в базе или используйте другую учётку."
          : raw;
      alert(msg || "Ошибка при удалении пользователя");
    }
  };

  const handleToggleAdmin = async (u, makeAdmin) => {
    if (
      makeAdmin &&
      !window.confirm(
        `Назначить администратором: ${u.email}?\nБудет доступ к панели /admin и ко всем данным.`,
      )
    )
      return;
    if (
      !makeAdmin &&
      !window.confirm(`Снять права администратора у ${u.email}?`)
    )
      return;
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/users/${u.id}/admin`, {
        is_admin: makeAdmin,
      });
      const demotedSelf =
        currentUser?.id === u.id && !makeAdmin;
      if (demotedSelf) {
        await checkAuth(false);
        window.location.assign("/");
        return;
      }
      await fetchUsers();
      if (currentUser?.id === u.id) {
        await checkAuth(false);
      }
    } catch (err) {
      alert(
        err?.response?.data?.error || "Не удалось изменить права администратора",
      );
    }
  };

  const handleToggleAnalytics = async (companyId, currentValue) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/companies/${companyId}/analytics`,
        {
          exclude: !currentValue,
        },
      );
      fetchUsers();
    } catch (err) {
      alert("Ошибка при обновлении настройки");
    }
  };

  if (loading && users.length === 0)
    return (
      <>
        <PageHeader title="Админка" subtitle="Панель администратора" />
        <div className="px-empty">Загрузка…</div>
      </>
    );

  const registrationRequests = users.filter((u) => u.status === "pending");
  const approvedUsers = users.filter((u) => u.status === "approved");
  const rejectedUsers = users.filter((u) => u.status === "rejected");

  const filteredApprovedUsers = approvedUsers.filter((u) => {
    if (!userSearchQuery.trim()) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      u.email?.toLowerCase().includes(query) ||
      u.company?.name?.toLowerCase().includes(query) ||
      u.company?.inn?.toLowerCase().includes(query)
    );
  });

  const TABS = [
    { key: "analytics", label: "Аналитика" },
    { key: "documents", label: "Документы" },
    { key: "users", label: "Пользователи", count: approvedUsers.length },
    {
      key: "requests",
      label: "Заявки",
      count: registrationRequests.length,
      attention: registrationRequests.length > 0,
    },
    { key: "rejected", label: "Отклонённые", count: rejectedUsers.length },
    { key: "settings", label: "Настройки" },
  ];

  const userIdentity = (u) => (
    <>
      <span className="adm-cell-main">
        {u.email}
        {u.is_admin && <span className="adm-badge">админ</span>}
      </span>
      <span className="adm-cell-sub">
        {u.company?.name}{" "}
        <span className="adm-nowrap">(ИНН: {u.company?.inn})</span>
      </span>
    </>
  );

  const deleteButton = (u) => (
    <button
      type="button"
      className="adm-icon-btn is-danger"
      disabled={!!u.is_admin}
      onClick={() => handleDeleteUser(u.id)}
      title={
        u.is_admin
          ? "Учётные записи администратора нельзя удалить"
          : "Удалить пользователя"
      }
      aria-label={
        u.is_admin
          ? `Удалить ${u.email}: учётные записи администратора нельзя удалить`
          : `Удалить пользователя ${u.email}`
      }
    >
      <Trash2 size={16} aria-hidden="true" />
    </button>
  );

  return (
    <>
      <PageHeader title="Админка" subtitle="Панель администратора" />

      <div className="adm-tabs" role="group" aria-label="Разделы админки">
        {TABS.map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              className={`hist-tab adm-tab${t.count === undefined ? " is-plain" : ""}${active ? " is-active" : ""}`}
              aria-pressed={active}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
              {t.count !== undefined && (
                <span
                  className={`hist-tab-count${t.attention && !active ? " adm-count-warn" : ""}`}
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <p role="alert" className="adm-alert">
          {error}
        </p>
      )}

      <div className="adm-body">
        {activeTab === "documents" && (
          <div>
            <div className="adm-controls" style={{ marginBottom: 24 }}>
                <div className="adm-card adm-control">
                  <p className="res-eyebrow">Период</p>
                  <DateRangePicker
                    initialRange={dateRange}
                    onChange={(newRange) => setDateRange(newRange)}
                  />
                  <p className="adm-hint">Период списка документов</p>
                </div>
                <div className="adm-card adm-control">
                  <label className="res-eyebrow adm-label" htmlFor="adm-company">
                    <Filter size={13} aria-hidden="true" /> Фильтр по компании
                  </label>
                  <select
                    id="adm-company"
                    className="rec-select adm-select"
                    value={selectedCompanyId}
                    onChange={(e) => setSelectedCompanyId(e.target.value)}
                  >
                    <option value="">Все компании</option>
                    {companiesList.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.inn})
                      </option>
                    ))}
                  </select>
                  <p className="adm-hint">
                    {selectedCompanyId
                      ? "Документы выбранной компании"
                      : "Документы всех компаний"}
                  </p>
                </div>
            </div>

            {documentsTotals && (
              <div className="adm-kpis" style={{ marginBottom: 32 }}>
                <Kpi
                  tone="lavender"
                  label="Документов"
                  value={documentsTotals.documents.toLocaleString()}
                />
                <Kpi
                  tone="mint"
                  label="Выручка"
                  value={`${documentsTotals.revenue_rub.toLocaleString()} ₽`}
                />
                <Kpi
                  tone="peach"
                  label="Потрачено на AI"
                  value={`${documentsTotals.ai_cost_rub.toLocaleString()} ₽`}
                  sub={`$${documentsTotals.ai_cost_usd.toFixed(2)} · курс ${documentsTotals.usd_rub_rate}`}
                />
                <Kpi
                  tone="sky"
                  label="Прибыль"
                  value={`${documentsTotals.profit_rub.toLocaleString()} ₽`}
                />
                <Kpi
                  label="AI на документ"
                  value={`${documentsTotals.avg_ai_cost_rub.toFixed(3)} ₽`}
                  sub={`${(
                    documentsTotals.input_tokens + documentsTotals.output_tokens
                  ).toLocaleString()} токенов`}
                />
              </div>
            )}

            {documentsLoading ? (
              <div className="px-empty">Загрузка…</div>
            ) : documents.length === 0 ? (
              <div className="px-empty">За выбранный период документов нет</div>
            ) : (
              <div className="px-table-wrap">
                <table className="adm-table" style={{ minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th scope="col">Дата</th>
                      <th scope="col">Документ</th>
                      <th scope="col">Компания</th>
                      <th scope="col" className="num">
                        Выручка, ₽
                      </th>
                      <th scope="col" className="num">
                        AI, ₽
                      </th>
                      <th scope="col" className="num">
                        Прибыль, ₽
                      </th>
                      <th scope="col" className="num">
                        Токены вх / исх
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id}>
                        <td className="adm-nowrap adm-soft">
                          {doc.created_at
                            ? new Date(doc.created_at).toLocaleString("ru-RU")
                            : "—"}
                        </td>
                        <td>
                          <span className="adm-cell-main">
                            {doc.filename || "—"}
                          </span>
                          <span className="adm-cell-sub" title={doc.document_type}>
                            {docTypeLabel(doc.document_type)}
                            {doc.country_code && doc.country_code !== "Unknown"
                              ? ` · ${doc.country_code}`
                              : ""}
                          </span>
                        </td>
                        <td>
                          <span className="adm-cell-main is-regular">
                            {doc.company_name || "—"}
                          </span>
                          <span className="adm-cell-sub">
                            {doc.user_email || ""}
                          </span>
                        </td>
                        <td className="num adm-strong">
                          {doc.revenue_rub.toLocaleString()}
                        </td>
                        <td className="num" title={`$${doc.ai_cost_usd}`}>
                          {doc.ai_cost_rub.toFixed(3)}
                        </td>
                        <td
                          className={`num adm-strong${doc.profit_rub < 0 ? " adm-negative" : ""}`}
                        >
                          {doc.profit_rub.toLocaleString()}
                        </td>
                        <td className="num adm-nowrap adm-soft">
                          {doc.input_tokens.toLocaleString()} /{" "}
                          {doc.output_tokens.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {documentsPagination && documentsPagination.pages > 1 && (
              <nav className="hist-pages adm-pages" aria-label="Страницы">
                <button
                  type="button"
                  className="px-icon-btn is-small"
                  aria-label="Назад"
                  disabled={documentsPage <= 1}
                  onClick={() => setDocumentsPage((p) => p - 1)}
                >
                  <ChevronLeft size={18} aria-hidden="true" />
                </button>
                <span className="hist-pages-info" aria-live="polite">
                  {documentsPagination.page} из {documentsPagination.pages}
                </span>
                <button
                  type="button"
                  className="px-icon-btn is-small"
                  aria-label="Вперёд"
                  disabled={documentsPage >= documentsPagination.pages}
                  onClick={() => setDocumentsPage((p) => p + 1)}
                >
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              </nav>
            )}
          </div>
        )}

        {activeTab === "requests" && (
          <div className="px-table-wrap">
            <table className="adm-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Компания / ИНН</th>
                  <th scope="col" className="num">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody>
                {registrationRequests.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="adm-empty-cell">
                      Нет новых заявок
                    </td>
                  </tr>
                ) : (
                  registrationRequests.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <span className="adm-cell-main">{u.email}</span>
                      </td>
                      <td>
                        <span className="adm-cell-main is-regular">
                          {u.company?.name}
                        </span>
                        <span className="adm-cell-sub">
                          ИНН: {u.company?.inn}
                        </span>
                      </td>
                      <td className="num">
                        <div className="adm-actions">
                          <button
                            type="button"
                            className="px-btn px-btn-ink adm-btn-sm"
                            onClick={() => handleStatusChange(u.id, "approved")}
                          >
                            <UserCheck size={16} aria-hidden="true" /> Одобрить
                          </button>
                          <button
                            type="button"
                            className="px-btn adm-btn-sm adm-btn-danger"
                            onClick={() => handleStatusChange(u.id, "rejected")}
                          >
                            <UserX size={16} aria-hidden="true" /> Отклонить
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "users" && (
          <div>
            <div className="adm-toolbar">
              <label className="hist-search adm-search">
                <Search size={18} aria-hidden="true" />
                <input
                  type="search"
                  aria-label="Поиск пользователей"
                  placeholder="Поиск по email, компании или ИНН..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                />
              </label>
              {userSearchQuery.trim() && (
                <span className="adm-muted" aria-live="polite">
                  Найдено: {filteredApprovedUsers.length}
                </span>
              )}
            </div>
            <div className="px-table-wrap">
              <table className="adm-table is-compact" style={{ minWidth: 1080 }}>
                <thead>
                  <tr>
                    <th scope="col">Пользователь / ИНН</th>
                    <th scope="col">Пополнение</th>
                    <th scope="col" className="num">
                      Баланс
                    </th>
                    <th scope="col">Тариф</th>
                    <th scope="col" className="num" title="Бесплатные распознавания">
                      Бесп.
                    </th>
                    <th scope="col" className="num" title="Распознано документов">
                      Расп.
                    </th>
                    <th scope="col" className="num">
                      Выручка
                    </th>
                    <th scope="col" className="adm-center" title="Учитывать в аналитике">
                      Аналитика
                    </th>
                    <th scope="col">
                      <span className="sr-only">Действия</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApprovedUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{userIdentity(u)}</td>
                      <td>
                        <div className="adm-inline">
                          <input
                            className="adm-input adm-input-sm"
                            type="number"
                            placeholder="±₽"
                            title="Положительное число — зачисление, отрицательное — списание с баланса"
                            aria-label={`Сумма пополнения или списания для ${u.email}, ₽`}
                            id={`topup-${u.id}`}
                            style={{ width: 104 }}
                          />
                          <button
                            type="button"
                            className="adm-icon-btn"
                            title="Изменить баланс"
                            aria-label={`Изменить баланс ${u.email}`}
                            onClick={() => {
                              const raw = document.getElementById(
                                `topup-${u.id}`,
                              ).value;
                              const val = parseFloat(
                                String(raw).replace(",", "."),
                              );
                              if (
                                raw === "" ||
                                !Number.isFinite(val) ||
                                val === 0
                              ) {
                                alert(
                                  "Введите ненулевую сумму: положительную (зачисление) или отрицательную (списание)",
                                );
                                return;
                              }
                              const sign = val >= 0 ? "+" : "";
                              if (
                                window.confirm(
                                  `Изменить баланс пользователя ${u.email} на ${sign}${val.toLocaleString("ru-RU")} ₽?`,
                                )
                              ) {
                                handleTopup(u.id, val);
                              }
                            }}
                          >
                            <Plus size={14} aria-hidden="true" />
                            <RussianRuble size={14} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                      <td className="num adm-strong adm-nowrap">
                        {u.balance.toLocaleString()} ₽
                      </td>
                      <td>
                        <div className="adm-inline">
                          <input
                            className="adm-input adm-input-sm"
                            type="number"
                            defaultValue={u.company?.price}
                            id={`price-${u.company?.id}`}
                            aria-label={`Тариф компании ${u.company?.name || u.email}, ₽ за страницу (паспорт и права — за документ)`}
                            style={{ width: 72 }}
                          />
                          <button
                            type="button"
                            className="adm-icon-btn"
                            onClick={() => {
                              const price = document.getElementById(
                                `price-${u.company?.id}`,
                              ).value;
                              handlePriceUpdate(u.company?.id, price);
                            }}
                            title="Сохранить тариф"
                            aria-label={`Сохранить тариф ${u.company?.name || u.email}`}
                          >
                            <Check size={16} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                      <td
                        className={`num${(u.company?.free_recognitions || 0) > 0 ? " adm-strong" : " adm-soft"}`}
                      >
                        {u.company?.free_recognitions || 0}
                      </td>
                      <td className="num">{u.record_count || 0}</td>
                      <td className="num adm-nowrap">
                        {(u.total_revenue || 0).toLocaleString()} ₽
                      </td>
                      <td className="adm-center">
                        <button
                          type="button"
                          className={`adm-icon-btn${u.company?.exclude_from_analytics ? " is-off" : " is-on is-soft"}`}
                          aria-pressed={!u.company?.exclude_from_analytics}
                          aria-label={`Учитывать ${u.company?.name || u.email} в аналитике`}
                          onClick={() =>
                            handleToggleAnalytics(
                              u.company?.id,
                              u.company?.exclude_from_analytics,
                            )
                          }
                          title={
                            u.company?.exclude_from_analytics
                              ? "Исключено из аналитики"
                              : "Учитывается в аналитике"
                          }
                        >
                          {u.company?.exclude_from_analytics ? (
                            <EyeOff size={16} aria-hidden="true" />
                          ) : (
                            <Eye size={16} aria-hidden="true" />
                          )}
                        </button>
                      </td>
                      <td>
                        <div className="adm-actions">
                          <button
                            type="button"
                            className="adm-icon-btn"
                            onClick={() => handleChangePassword(u.id, u.email)}
                            title="Изменить пароль"
                            aria-label={`Изменить пароль ${u.email}`}
                          >
                            <KeyRound size={16} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={`adm-icon-btn${u.is_admin ? " is-on" : ""}`}
                            aria-pressed={!!u.is_admin}
                            onClick={() => handleToggleAdmin(u, !u.is_admin)}
                            title={
                              u.is_admin
                                ? "Снять права администратора"
                                : "Назначить администратором"
                            }
                            aria-label={`Администратор: ${u.email}`}
                          >
                            <Shield size={16} aria-hidden="true" />
                          </button>
                          {deleteButton(u)}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredApprovedUsers.length === 0 && (
                    <tr>
                      <td colSpan="9" className="adm-empty-cell">
                        {userSearchQuery.trim()
                          ? "Никого не нашли"
                          : "Пользователей пока нет"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "rejected" && (
          <div className="px-table-wrap">
            <table className="adm-table is-compact" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th scope="col">Пользователь / ИНН</th>
                  <th scope="col">Дата регистрации</th>
                  <th scope="col">
                    <span className="sr-only">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rejectedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="adm-empty-cell">
                      Нет отклонённых пользователей
                    </td>
                  </tr>
                ) : (
                  rejectedUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{userIdentity(u)}</td>
                      <td className="adm-soft adm-nowrap">
                        {u.created_at
                          ? new Date(u.created_at).toLocaleDateString("ru-RU")
                          : "—"}
                      </td>
                      <td>
                        <div className="adm-actions">
                          <button
                            type="button"
                            className="px-btn px-btn-outline adm-btn-sm"
                            onClick={() => handleStatusChange(u.id, "approved")}
                          >
                            <UserCheck size={16} aria-hidden="true" />
                            Восстановить
                          </button>
                          {deleteButton(u)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "analytics" && (
          <AdminAnalytics
            companies={companiesList}
            pendingRequests={registrationRequests.length}
          />
        )}

        {activeTab === "settings" && (
          <div className="adm-controls">
                <div className="adm-card adm-control">
                  <p className="res-eyebrow" id="adm-rate-label">
                    Курс USD/RUB
                  </p>
                  {editingRate ? (
                    <form
                      className="adm-edit"
                      onSubmit={(e) => {
                        e.preventDefault();
                        updateRate();
                      }}
                    >
                      <input
                        type="number"
                        step="0.01"
                        className="adm-input"
                        aria-labelledby="adm-rate-label"
                        value={newRate}
                        onChange={(e) => setNewRate(e.target.value)}
                      />
                      <button type="submit" className="px-btn px-btn-ink adm-btn-sm">
                        Сохранить
                      </button>
                      <button
                        type="button"
                        className="px-btn px-btn-outline adm-btn-sm"
                        onClick={() => {
                          setEditingRate(false);
                          setNewRate(usdRubRate.toString());
                        }}
                      >
                        Отмена
                      </button>
                    </form>
                  ) : (
                    <div className="adm-setting">
                      <span className="adm-setting-value">
                        {usdRubRate.toFixed(2)} ₽
                      </span>
                      <button
                        type="button"
                        className="px-btn px-btn-outline adm-btn-sm"
                        aria-label="Изменить курс USD/RUB"
                        onClick={() => setEditingRate(true)}
                      >
                        <Pencil size={14} aria-hidden="true" /> Изменить
                      </button>
                    </div>
                  )}
                  <p className="adm-hint">
                    Используется для расчета стоимости Gemini API
                  </p>
                </div>
                <div className="adm-card adm-control">
                  <p className="res-eyebrow" id="adm-page-cost-label">
                    Стоимость 1 страницы
                  </p>
                  {editingPageCost ? (
                    <form
                      className="adm-edit"
                      onSubmit={(e) => {
                        e.preventDefault();
                        updatePageCost();
                      }}
                    >
                      <input
                        type="number"
                        step="1"
                        min="0"
                        className="adm-input"
                        aria-labelledby="adm-page-cost-label"
                        value={newPageCost}
                        onChange={(e) => setNewPageCost(e.target.value)}
                      />
                      <button type="submit" className="px-btn px-btn-ink adm-btn-sm">
                        Сохранить
                      </button>
                      <button
                        type="button"
                        className="px-btn px-btn-outline adm-btn-sm"
                        onClick={() => {
                          setEditingPageCost(false);
                          setNewPageCost(pageCost.toString());
                        }}
                      >
                        Отмена
                      </button>
                    </form>
                  ) : (
                    <div className="adm-setting">
                      <span className="adm-setting-value">
                        {pageCost.toFixed(0)} ₽
                      </span>
                      <button
                        type="button"
                        className="px-btn px-btn-outline adm-btn-sm"
                        aria-label="Изменить стоимость перевода страницы"
                        onClick={() => setEditingPageCost(true)}
                      >
                        <Pencil size={14} aria-hidden="true" /> Изменить
                      </button>
                    </div>
                  )}
                  <p className="adm-hint">
                    Стоимость перевода 1 страницы документа. Списывается с
                    баланса при переводе документов (договоров)
                  </p>
                </div>
          </div>
        )}
      </div>

    </>
  );
};

export default AdminDashboard;
